import { useEffect, useId, useRef } from "react";

import { TURNSTILE_SITE_KEY, TURNSTILE_TEST_SITE_KEY } from "./turnstile-shared";
import { isPreviewBrowserHost } from "@/lib/forms/lead-host";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: (code?: string) => void;
          "expired-callback"?: () => void;
          "timeout-callback"?: () => void;
          "unsupported-callback"?: () => void;
          "before-interactive-callback"?: () => void;
          "after-interactive-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "invisible";
          action?: string;
          "refresh-expired"?: "auto" | "manual" | "never";
          retry?: "auto" | "never";
          appearance?: "always" | "execute" | "interaction-only";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
// After this long with no Turnstile API, tell the form (audit F2, 5 Oct 2026).
// The load keeps going: if the script arrives later, the widget still renders.
const SCRIPT_TIMEOUT_MS = 10_000;
// Background retry after a widget or script error, for callers that set
// `backoff` (17 Oct 2026). Cloudflare's own retry tried every few seconds,
// and with the caller's resets on top a desktop visitor saw about 10 errors
// in a row. Waits grow instead; a token starts the count again.
// At most 2 retries since 23 Oct 2026 (was 5 over 93 s). Quick quote sends
// without a token after an error anyway, so later retries only added events.
const BACKOFF_MS = [5_000, 20_000] as const;

/**
 * Load the Turnstile API once. Calls onLoad when window.turnstile exists.
 * Calls onFail at most once if the script fails to load (a blocker or a
 * network error) or is not there after SCRIPT_TIMEOUT_MS. Before 6 Oct a
 * failed load left the visitor on "One moment, your quote will send
 * automatically" with no end. Returns a cleanup function.
 */
function loadTurnstileScript(onLoad: () => void, onFail: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  if (window.turnstile) {
    onLoad();
    return () => {};
  }
  let script = document.querySelector<HTMLScriptElement>(
    `script[src^="https://challenges.cloudflare.com/turnstile"]`,
  );
  // A tag that failed before never loads again. Replace it, so a retry
  // makes a new request.
  if (script?.dataset.rhFailed === "1") {
    script.remove();
    script = null;
  }
  if (!script) {
    script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
  const tag = script;
  let failed = false;
  const fail = () => {
    if (failed || window.turnstile) return;
    failed = true;
    onFail();
  };
  const handleLoad = () => {
    window.clearTimeout(timer);
    if (window.turnstile) onLoad();
  };
  const handleError = () => {
    tag.dataset.rhFailed = "1";
    window.clearTimeout(timer);
    fail();
  };
  tag.addEventListener("load", handleLoad, { once: true });
  tag.addEventListener("error", handleError, { once: true });
  const timer = window.setTimeout(fail, SCRIPT_TIMEOUT_MS);
  return () => {
    window.clearTimeout(timer);
    tag.removeEventListener("load", handleLoad);
    tag.removeEventListener("error", handleError);
  };
}

export interface TurnstileProps {
  onToken: (token: string) => void;
  onExpired?: () => void;
  /** Receives the Turnstile client-side error code, e.g. "300030". */
  onError?: (code: string) => void;
  /** The challenge timed out before the visitor finished it. */
  onTimeout?: () => void;
  /** The browser does not support Turnstile. */
  onUnsupported?: () => void;
  /** Turnstile is about to show the visitor an interactive challenge. */
  onBeforeInteractive?: () => void;
  /** The interactive challenge is finished. */
  onAfterInteractive?: () => void;
  action?: string;
  /**
   * "interaction-only" keeps the widget hidden unless Cloudflare needs the
   * visitor to click. Default "always" (Cloudflare's default) for other forms.
   */
  appearance?: "always" | "interaction-only";
  /** Set to a function that resets the widget, so the caller can retry. */
  resetRef?: { current: (() => void) | null };
  /**
   * Wait for the visitor before loading Turnstile. The script and its
   * challenge are about 715 KB, half of the quote page's weight (PageSpeed,
   * 9 Oct 2026), and nobody can submit before touching the form. With this
   * set, loading starts on the first tap or key press anywhere on the page,
   * on focus of a form field, or when the widget's place comes within
   * 300 px of the screen, whichever is first. A reset from the caller also
   * starts it.
   */
  lazy?: boolean;
  /**
   * Retry errors in the background with growing waits (BACKOFF_MS), and turn
   * off Cloudflare's own fast retry. Off by default: other forms keep
   * Cloudflare's retry.
   */
  backoff?: boolean;
}

export function Turnstile({
  onToken,
  onExpired,
  onError,
  onTimeout,
  onUnsupported,
  onBeforeInteractive,
  onAfterInteractive,
  action,
  appearance = "always",
  resetRef,
  lazy = false,
  backoff = false,
}: TurnstileProps) {
  const containerId = useId().replace(/:/g, "-");
  const widgetIdRef = useRef<string | null>(null);
  const callbacksRef = useRef({
    onToken,
    onExpired,
    onError,
    onTimeout,
    onUnsupported,
    onBeforeInteractive,
    onAfterInteractive,
  });

  useEffect(() => {
    callbacksRef.current = {
      onToken,
      onExpired,
      onError,
      onTimeout,
      onUnsupported,
      onBeforeInteractive,
      onAfterInteractive,
    };
  }, [onToken, onExpired, onError, onTimeout, onUnsupported, onBeforeInteractive, onAfterInteractive]);

  useEffect(() => {
    let cancelled = false;
    // A non-production host (a branch preview) uses Cloudflare's always-pass
    // test key: the production key is not valid on workers.dev, and the
    // server skips the Turnstile check on that host (8 Oct 2026).
    const siteKey = isPreviewBrowserHost()
      ? TURNSTILE_TEST_SITE_KEY
      : import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() || TURNSTILE_SITE_KEY;
    let stopLoad: () => void = () => {};
    // Background retry (backoff only).
    let retryTimer: number | undefined;
    let retries = 0;
    const clearRetry = () => {
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      retryTimer = undefined;
    };
    const scheduleRetry = () => {
      if (!backoff || cancelled || retryTimer !== undefined) return;
      const wait = BACKOFF_MS[retries];
      if (wait === undefined) return;
      retries += 1;
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        retryNow();
      }, wait);
    };

    const render = () => {
      if (cancelled || !window.turnstile || widgetIdRef.current) return;
      const el = document.getElementById(containerId);
      if (!el) return;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        action,
        theme: "auto",
        size: "normal",
        // Renew the token before its 300 s expiry (8 Oct 2026).
        "refresh-expired": "auto",
        ...(backoff ? { retry: "never" as const } : {}),
        appearance,
        callback: (token) => {
          retries = 0;
          clearRetry();
          callbacksRef.current.onToken(token);
        },
        "expired-callback": () => callbacksRef.current.onExpired?.(),
        "timeout-callback": () => callbacksRef.current.onTimeout?.(),
        "unsupported-callback": () => callbacksRef.current.onUnsupported?.(),
        "error-callback": (code) => {
          callbacksRef.current.onError?.(String(code ?? "unknown"));
          scheduleRetry();
        },
        "before-interactive-callback": () => callbacksRef.current.onBeforeInteractive?.(),
        "after-interactive-callback": () => callbacksRef.current.onAfterInteractive?.(),
      });
    };
    const load = () => {
      stopLoad();
      stopLoad = loadTurnstileScript(render, () => {
        if (cancelled) return;
        callbacksRef.current.onError?.("script_load");
        scheduleRetry();
      });
    };
    // Lazy: start on the visitor's first interaction or when the widget is
    // about to scroll into view. Otherwise start now, as before.
    let started = false;
    let stopWaiting: () => void = () => {};
    const start = () => {
      if (started || cancelled) return;
      started = true;
      stopWaiting();
      load();
    };
    if (!lazy) {
      start();
    } else {
      const events = ["pointerdown", "keydown", "touchstart"] as const;
      const opts = { capture: true, passive: true } as const;
      // Only a real tap or key press counts. Focus counts only on a form
      // field: the OneTrust banner takes focus by script when it appears,
      // which started the load on every page view (found on production,
      // 9 Oct 2026).
      const onInput = (e: Event) => {
        if (e.isTrusted) start();
      };
      const onFocus = (e: Event) => {
        const t = e.target as Element | null;
        if (t?.closest?.("form") && t.matches?.("input, select, textarea")) start();
      };
      for (const e of events) document.addEventListener(e, onInput, opts);
      document.addEventListener("focusin", onFocus, opts);
      const el = document.getElementById(containerId);
      const io =
        el && "IntersectionObserver" in window
          ? new IntersectionObserver(
              (entries) => {
                if (entries.some((x) => x.isIntersecting)) start();
              },
              { rootMargin: "300px" },
            )
          : null;
      if (el && io) io.observe(el);
      // No observer support: do not wait.
      if (!io) start();
      stopWaiting = () => {
        for (const e of events) document.removeEventListener(e, onInput, opts);
        document.removeEventListener("focusin", onFocus, opts);
        io?.disconnect();
      };
    }

    // Reset the widget, or start or retry the script load.
    function retryNow() {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {
          // ignore
        }
        return;
      }
      if (cancelled) return;
      if (!started) start();
      else load();
    }

    if (resetRef) {
      resetRef.current = () => {
        // A reset from the caller replaces a pending background retry.
        clearRetry();
        retryNow();
      };
    }

    return () => {
      cancelled = true;
      clearRetry();
      stopWaiting();
      stopLoad();
      if (resetRef) resetRef.current = null;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, [containerId, action, appearance, resetRef, lazy, backoff]);

  return <div id={containerId} className="cf-turnstile mt-2" />;
}
